package com.essntl.features.request.ui.allrequest

import com.essntl.core.utils.utils.loadingmanager.LoadingManager
import com.essntl.core.utils.utils.update
import com.essntl.core.utils.viewmodel.PaginationState
import com.essntl.core.utils.viewmodel.State
import com.essntl.core.utils.viewmodel.ViewModel
import com.essntl.core.utils.viewmodel.getData
import com.essntl.core.utils.viewmodel.getPaginationData
import com.essntl.features.area.domain.repository.AreaRepository
import com.essntl.features.profile.domain.repository.ProfileRepository
import com.essntl.features.proposal.domain.repository.ProposalRepository
import com.essntl.features.request.domain.model.RequestFilterOptions
import com.essntl.features.request.domain.model.RequestModel
import com.essntl.features.request.domain.repository.RequestRepository
import com.essntl.toastmanager.ToastManager
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class AllRequestViewModel(
    private val requestRepository: RequestRepository,
    private val areaRepository: AreaRepository,
    private val proposalRepository: ProposalRepository,
    private val profileRepository: ProfileRepository,
    private val state: MutableStateFlow<AllRequestState>,
    private val toastManager: ToastManager,
    override val scope: CoroutineScope,
    override val loadingManager: LoadingManager,
) : ViewModel {
    init {
        getAllArea()
        getAllProfile()
    }

    fun onEvent(event: AllRequestEvent) {
        when (event) {
            is AllRequestEvent.UpdateRequestStatus -> updateRequestStatus(event)
            is AllRequestEvent.GetAllRequest -> getAllRequests()
            is AllRequestEvent.LoadMoreRequests -> loadMoreRequests()
            is AllRequestEvent.FilterRequest -> filterRequest(event.filterOptions)
            is AllRequestEvent.UpdateRequestOwner -> updateRequestOwner(event)
        }
    }

    private fun getAllProfile() =
        getData(
            getData = { profileRepository.getAll() },
            getState = { state.value.getAllProfile },
            setState = { newState -> state.update { it.copy(getAllProfile = newState) } },
        )

    private fun getAllRequests() {
        state.update {
            it.copy(getAllRequest = PaginationState.loading())
        }

        loadMoreRequests(ignoreLoading = true)
    }

    private var loadMoreRequestsJob: Job? = null
    private fun loadMoreRequests(ignoreLoading: Boolean = false) {
        if (!ignoreLoading && state.value.getAllRequest.isLoading) return

        loadMoreRequestsJob?.cancel()
        loadMoreRequestsJob = getPaginationData(
            getData = {
                val nextPage = state.value.getAllRequest.loadedPages + 1L
                requestRepository.getAll(
                    page = nextPage,
                    filterOptions = state.value.filterOptions,
                )
            },
            getState = { state.value.getAllRequest },
            setState = { newState -> state.update { it.copy(getAllRequest = newState) } },
            onSuccess = { _, requests ->
                val proposalIds = requests
                    .mapNotNull { it.proposalId }
                    .filter { id -> state.value.getAllProposal.data.orEmpty().none { id == it.id } }

                if (proposalIds.isNotEmpty())
                    getProposals(proposalIds.toSet())

            },
            ignoreLoading = ignoreLoading,
        )
    }

    private fun getProposals(ids: Set<String>) =
        getData(
            getData = { proposalRepository.getAllProposalByIds(ids.toList()) },
            getState = { state.value.getAllProposal },
            setState = { newState ->
                if (!newState.isSuccess)
                    state.update {
                        it.copy(
                            getAllProposal = newState,
                        )
                    }
            },
            onSuccess = { newProposals ->
                state.update {
                    it.copy(
                        getAllProposal = State.success(it.getAllProposal.data.orEmpty() + newProposals),
                    )
                }
            },
        )

    private fun filterRequest(filterOptions: RequestFilterOptions) {
        if (filterOptions == state.value.filterOptions) return

        state.update {
            it.copy(
                filterOptions = filterOptions,
            )
        }

        getAllRequests()
    }

    private fun updateRequestStatus(event: AllRequestEvent.UpdateRequestStatus) {
        if (state.value.updateRequestLoading.contains(event.requestId)) return

        state.value = state.value.copy(
            updateRequestLoading = state.value.updateRequestLoading + event.requestId,
        )

        val request = state.value.getAllRequest.data.find {
            it.id == event.requestId
        }?.copy(status = event.status) ?: RequestModel.empty.copy(
            id = event.requestId,
            status = event.status,
        )

        scope.launch {
            requestRepository.update(request)
                .onSuccess { request ->
                    val requestList = state.value.getAllRequest.data.orEmpty().toMutableList()
                    val requestIndex = requestList.indexOfFirst { it.id == request.id }
                    if (requestIndex != -1)
                        requestList[requestIndex] = request

                    state.value = state.value.copy(
                        getAllRequest = state.value.getAllRequest.copy(
                            data = requestList,
                        ),
                        updateRequestLoading = state.value.updateRequestLoading - event.requestId,
                    )

                    toastManager.success(description = "Request status updated successfully")
                }
                .onFailure {
                    state.value = state.value.copy(
                        updateRequestLoading = state.value.updateRequestLoading - event.requestId,
                    )
                }
        }
    }

    private fun getAllArea() =
        getData(
            getData = { areaRepository.getAll() },
            getState = { state.value.getAllArea },
            setState = { newState -> state.update { it.copy(getAllArea = newState) } },
        )

    private fun updateRequestOwner(event: AllRequestEvent.UpdateRequestOwner) {
        // Prevent multiple requests for the same request
        if (event.requestId in state.value.updateRequestLoading) return

        // Check if the new owner is the same as the current owner
        val request = state.value.getAllRequest.data.find { it.id == event.requestId }
        if (request?.ownerId == event.ownerId) return

        state.value = state.value.copy(
            updateRequestLoading = state.value.updateRequestLoading + event.requestId,
        )

        val newRequest = request?.copy(
            ownerId = event.ownerId,
        ) ?: RequestModel.empty.copy(
            id = event.requestId,
            ownerId = event.ownerId,
        )

        scope.launch {
            requestRepository.update(newRequest)
                .onSuccess { request ->
                    val requestList = state.value.getAllRequest.data.update(
                        predicate = { it.id == request.id },
                        update = { request },
                    )

                    state.value = state.value.copy(
                        getAllRequest = state.value.getAllRequest.copy(
                            data = requestList,
                        ),
                        updateRequestLoading = state.value.updateRequestLoading - event.requestId,
                    )
                }
                .onFailure {
                    state.value = state.value.copy(
                        updateRequestLoading = state.value.updateRequestLoading - event.requestId,
                    )
                }
        }
    }
}